import { useState, useCallback, useEffect, useRef } from 'react';
import { startScrapeJob, getScrapeStatus, listProfiles } from '../api/scrape';
import { on } from '../api/events';
import { useActiveProfile } from '../context/ActiveProfileContext';

export default function useScrapeJob() {
  const { activeProfileId: globalProfileId, setActiveProfileId: setGlobalProfileId } = useActiveProfile();
  const [status, setStatus] = useState('loading'); // 'loading' | null | 'pending' | 'completed' | 'failed'
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [currentJobId, setCurrentJobId] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const data = await listProfiles();
      const profiles = (data.profiles || []).filter(p => !p.deleted);
      setHistory(profiles);
      return profiles;
    } catch {
      return [];
    }
  }, []);

  const refreshLatest = useCallback(async () => {
    const profiles = await loadHistory();
    if (profiles.length > 0) {
      // Use the globally selected profile if it exists and is valid, otherwise use latest
      const targetId = globalProfileId && profiles.some(p => p.jobId === globalProfileId)
        ? globalProfileId
        : profiles[0].jobId;
      try {
        const data = await getScrapeStatus(targetId);
        if (data.profile) {
          setProfile(data.profile);
          setCurrentJobId(targetId);
          setStatus('completed');
          setGlobalProfileId(targetId);
          return;
        }
      } catch { /* fall through */ }
    }
    setStatus(null);
  }, [loadHistory, globalProfileId, setGlobalProfileId]);

  // On mount: load latest profile
  useEffect(() => {
    refreshLatest();
    return stopPolling;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for profile updates from chat
  useEffect(() => {
    return on('profile-updated', refreshLatest);
  }, [refreshLatest]);

  // Poll for job completion
  const startPolling = useCallback((jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const data = await getScrapeStatus(jobId);
        if (data.status === 'completed' && data.profile) {
          stopPolling();
          setProfile(data.profile);
          setCurrentJobId(jobId);
          setStatus('completed');
          setGlobalProfileId(jobId);
          loadHistory();
        } else if (data.status === 'failed') {
          stopPolling();
          setStatus('failed');
          setError(data.error || 'Analysis failed.');
        }
        // else still pending/running, keep polling
      } catch (err) {
        console.warn('Poll error:', err.message);
      }
    }, 3000);
  }, [stopPolling, loadHistory, setGlobalProfileId]);

  const startScrape = useCallback(async (url, extras = {}) => {
    stopPolling();
    setStatus('pending');
    setProfile(null);
    setError(null);

    try {
      const data = await startScrapeJob(url, extras);
      setCurrentJobId(data.jobId);

      if (data.status === 'completed' && data.profile) {
        setStatus('completed');
        setProfile(data.profile);
        setGlobalProfileId(data.jobId);
        loadHistory();
      } else {
        // Job is async — start polling
        setStatus('pending');
        startPolling(data.jobId);
      }
    } catch (err) {
      setStatus('failed');
      if (err.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else if (err.status === 502) {
        setError('AI service is temporarily unavailable. Please try again later.');
      } else if (err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    }
  }, [stopPolling, startPolling, loadHistory, setGlobalProfileId]);

  const loadProfile = useCallback(async (jobId) => {
    setStatus('pending');
    setError(null);
    try {
      const data = await getScrapeStatus(jobId);
      if (data.profile) {
        setProfile(data.profile);
        setCurrentJobId(jobId);
        setStatus('completed');
        setGlobalProfileId(jobId);
      } else {
        setError('Profile data not found.');
        setStatus('failed');
      }
    } catch (err) {
      setError(err.message || 'Failed to load profile.');
      setStatus('failed');
    }
  }, [setGlobalProfileId]);

  const deleteProfile = useCallback(async (jobId) => {
    try {
      // Soft delete via status update
      const { apiPost } = await import('../api/client');
      await apiPost(`/scrape/${jobId}/delete`, {});
    } catch { /* ignore */ }
    // Update local state
    setHistory(prev => prev.filter(p => p.jobId !== jobId));
    if (currentJobId === jobId) {
      setProfile(null);
      setCurrentJobId(null);
      setStatus(null);
    }
  }, [currentJobId]);

  const reset = useCallback(() => {
    stopPolling();
    setStatus(null);
    setProfile(null);
    setError(null);
    setCurrentJobId(null);
  }, [stopPolling]);

  return { status, profile, error, history, currentJobId, startScrape, loadProfile, deleteProfile, reset };
}
